import { useEffect, useState } from 'react';
import useScrollReveal from '../hooks/useScrollReveal';
import { requestContactPrefill } from '../utils/contactPrefill';
import './CareersPage.css';

/* ─────────────────────────────────────────────────────────────
   What working here means
───────────────────────────────────────────────────────────── */

const workAreas = [
  {
    number: '01',
    title: 'Build meaningful products',
    description:
      'Work on products and intelligent systems that solve real problems. From early ideas to production, we care about creating technology that becomes genuinely useful to people.',
  },
  {
    number: '02',
    title: 'Think beyond the feature',
    description:
      'We care about the systems underneath the interface: intelligence, memory, reasoning, context, reliability, safety, infrastructure, and how every part works together.',
  },
  {
    number: '03',
    title: 'Own the outcome',
    description:
      'Understand the problem, make thoughtful decisions, build with care, measure what happens, and keep improving. Great work does not end when the code is merged.',
  },
];

/* ─────────────────────────────────────────────────────────────
   Candidate qualities
───────────────────────────────────────────────────────────── */

const qualities = [
  {
    number: '01',
    title: 'Leadership',
    headline: 'Lead before you are asked.',
    description:
      'Leadership here is not a title. It is the ability to create clarity, make thoughtful decisions, take responsibility for outcomes, communicate direction, and help the people around you do better work.',
  },
  {
    number: '02',
    title: 'Ownership',
    headline: 'Care about the outcome.',
    description:
      'Do not stop at completing a task. Understand why the work matters, follow it through, notice what is missing, and take responsibility for the quality of what reaches the user.',
  },
  {
    number: '03',
    title: 'Judgement',
    headline: 'Know when to move and when to think.',
    description:
      'Good product work requires both speed and restraint. We value people who can work through ambiguity, challenge assumptions, use evidence, and change their mind when better information appears.',
  },
  {
    number: '04',
    title: 'Curiosity',
    headline: 'Go deeper than the obvious answer.',
    description:
      'Ask why. Learn unfamiliar systems. Explore the problem before becoming attached to a solution. Strong builders stay students even when they become experts.',
  },
];

/* ─────────────────────────────────────────────────────────────
   Operating principles
───────────────────────────────────────────────────────────── */

const principles = [
  {
    number: '01',
    title: 'Start with the user',
    description:
      'Technology matters when it creates a better experience for the person using it. We work backwards from the problem, not forwards from the technology.',
  },
  {
    number: '02',
    title: 'Build systems, not patches',
    description:
      'We prefer clear foundations over layers of temporary fixes. Architecture should make our products easier to understand, extend, and operate over time.',
  },
  {
    number: '03',
    title: 'Seek truth',
    description:
      'Strong opinions are welcome. Evidence matters more. We question assumptions, surface problems early, and update our thinking when reality disagrees with us.',
  },
  {
    number: '04',
    title: 'Ship with purpose',
    description:
      'Progress matters, but activity is not progress. We move with urgency when the direction is clear and take the time to think when the decision deserves it.',
  },
];

const futureAreas = [
  'AI Engineering',
  'Product Engineering',
  'AI Research',
  'Product',
  'Design',
];

const CareersPage = () => {
  const [mounted, setMounted] = useState(false);

  useScrollReveal({
    threshold: 0.08,
    rootMargin: '-50px',
    oneWay: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 60);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });

    return () => clearTimeout(timer);
  }, []);

  return (
    <main className="cp-page">

      {/* ═══════════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════════ */}

      <section className="cp-hero">
        <div className="container">

          <div className="cp-hero-grid">

            <div
              className="cp-hero-content"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'none' : 'translateY(40px)',
                transition: 'all 1s var(--ease-out) 0.15s',
              }}
            >
              <span className="cp-eyebrow">
                Careers at Allyverto
              </span>

              <h1 className="cp-hero-title">
                Build intelligence
                <br />
                that <em>matters.</em>
              </h1>

              <p className="cp-hero-description">
                Rusiv is the first thing we've shipped, not the only thing
                we're building. Join us to help shape the personal
                intelligence platform underneath it, and whatever comes
                after it.
              </p>

              <div className="cp-hero-actions">

                <a
                  href="#opportunities"
                  className="cp-primary-btn"
                >
                  View opportunities

                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 8h9M9 4.5 12.5 8 9 11.5"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>

                <a
                  href="#how-we-work"
                  className="cp-secondary-btn"
                >
                  How we work
                </a>

              </div>
            </div>

            <div
              className="cp-hero-side"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'none' : 'translateY(28px)',
                transition: 'all 1s var(--ease-out) 0.35s',
              }}
            >
              <div className="cp-hero-statement">

                <span className="cp-hero-statement-line" />

                <p>
                  We look for people who think independently,
                  work deeply, lead with responsibility, and care
                  about the quality of what they put into the world.
                </p>

              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          THE WORK
      ═══════════════════════════════════════════════════════ */}

      <section className="cp-section cp-work-section">
        <div className="container">

          <div className="cp-work-header" data-scroll>

            <div className="cp-work-label-col">
              <span className="cp-eyebrow">
                The work
              </span>
            </div>

            <div className="cp-work-heading-col">

              <h2 className="cp-section-title">
                Build the product.
                <br />
                <em>Own the outcome.</em>
              </h2>

              <p className="cp-section-description">
                Allyverto is a product company. We explore difficult
                technological problems and turn the strongest ideas into
                products and systems that create real value for people.
              </p>

            </div>

          </div>


          <div className="cp-work-grid">

            {workAreas.map((item, index) => (
              <article
                key={item.number}
                className="cp-work-card"
                data-scroll
                style={{
                  transitionDelay: `${index * 0.08}s`,
                }}
              >
                <span className="cp-card-number">
                  {item.number}
                </span>

                <div className="cp-work-card-content">

                  <h3>{item.title}</h3>

                  <p>
                    {item.description}
                  </p>

                </div>
              </article>
            ))}

          </div>

        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          WHO THRIVES HERE
      ═══════════════════════════════════════════════════════ */}

      <section
        className="cp-section cp-people-section"
        id="how-we-work"
      >
        <div className="container">

          <div className="cp-standard-header" data-scroll>

            <span className="cp-eyebrow">
              Who thrives here
            </span>

            <h2 className="cp-section-title">
              Skill opens the door.
              <br />
              <em>Judgement keeps you here.</em>
            </h2>

            <p className="cp-section-description">
              Strong technical ability matters. So does the way you think,
              communicate, make decisions, take ownership, and lead when
              the path is not obvious.
            </p>

          </div>


          <div className="cp-quality-list">

            {qualities.map((quality, index) => (
              <article
                key={quality.number}
                className={`cp-quality-row ${
                  quality.title === 'Leadership'
                    ? 'cp-quality-row-featured'
                    : ''
                }`}
                data-scroll
                style={{
                  transitionDelay: `${index * 0.07}s`,
                }}
              >

                <div className="cp-quality-number">
                  {quality.number}
                </div>

                <div className="cp-quality-name">
                  {quality.title}
                </div>

                <div className="cp-quality-content">

                  <h3>
                    {quality.headline}
                  </h3>

                  <p>
                    {quality.description}
                  </p>

                </div>

              </article>
            ))}

          </div>


          <div className="cp-leadership-note" data-scroll>

            <div className="cp-leadership-mark">
              L
            </div>

            <div className="cp-leadership-content">

              <span>
                Our view of leadership
              </span>

              <p>
                You do not need direct reports to be a leader.
                Engineers can lead architecture. Designers can lead
                product clarity. Researchers can lead understanding.
                Product builders can lead decisions. Leadership begins
                when you take responsibility beyond the boundaries
                of your individual task.
              </p>

            </div>

          </div>

        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          OPERATING PRINCIPLES
      ═══════════════════════════════════════════════════════ */}

      <section className="cp-section cp-principles-section">
        <div className="container">

          <div className="cp-principles-layout">

            <div className="cp-principles-heading" data-scroll>

              <span className="cp-eyebrow">
                How we build
              </span>

              <h2 className="cp-section-title">
                Principles over
                <br />
                <em>process.</em>
              </h2>

              <p className="cp-section-description">
                Processes will evolve as Allyverto grows.
                The principles behind good work should remain clear.
              </p>

            </div>


            <div className="cp-principles-list">

              {principles.map((principle, index) => (
                <article
                  className="cp-principle-row"
                  key={principle.number}
                  data-scroll
                  style={{
                    transitionDelay: `${index * 0.07}s`,
                  }}
                >

                  <span className="cp-principle-number">
                    {principle.number}
                  </span>

                  <div>

                    <h3>
                      {principle.title}
                    </h3>

                    <p>
                      {principle.description}
                    </p>

                  </div>

                </article>
              ))}

            </div>

          </div>

        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════
          OPPORTUNITIES
      ═══════════════════════════════════════════════════════ */}

      <section
        className="cp-opportunities"
        id="opportunities"
      >
        <div className="container">

          <div className="cp-opportunities-grid">

            <div
              className="cp-opportunities-main"
              data-scroll
            >

              <div className="cp-status">
                <span className="cp-status-dot" />
                No open roles currently
              </div>

              <h2 className="cp-opportunities-title">
                No opening today.
                <br />
                <em>Still worth meeting.</em>
              </h2>

              <p className="cp-opportunities-description">
                We are not actively hiring at the moment. But if the way
                we think and build sounds like an environment where you
                could do meaningful work, you can introduce yourself to us.
              </p>

              <button
                type="button"
                className="cp-opportunity-btn"
                data-bs-toggle="modal"
                data-bs-target="#getintouchandcontct"
              >
                Introduce yourself

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8h9M9 4.5 12.5 8 9 11.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

              </button>

            </div>


            <aside
              className="cp-opportunities-side"
              data-scroll
            >

              <span className="cp-opportunities-side-label">
                Areas we expect to grow
              </span>

              <div className="cp-role-list">

                {futureAreas.map((area) => (
                  <button
                    type="button"
                    className="cp-role-item"
                    key={area}
                    data-bs-toggle="modal"
                    data-bs-target="#getintouchandcontct"
                    onClick={() =>
                      requestContactPrefill({
                        subject: 'career',
                        message: `I'm interested in opportunities in ${area}.`,
                      })
                    }
                    aria-label={`Introduce yourself for ${area} opportunities`}
                  >

                    <span>{area}</span>

                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 7h8M8 4l3 3-3 3"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                  </button>
                ))}

              </div>

              <p className="cp-opportunities-note">
                These are areas of interest, not currently open positions.
              </p>

            </aside>

          </div>

        </div>
      </section>

    </main>
  );
};

export default CareersPage;